import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { brand, shadows } from '../theme';
import Img from './Img';

/**
 * One photo card: the image on a white rounded card with its caption beneath,
 * lifting slightly on hover.
 *
 * This is THE card — the gallery page and the home page's "Recent work" grid
 * both render it, so the two can never drift apart in style. It exists because
 * they briefly were two hand-written copies, and the home one aged worse.
 *
 * Pass onActivate to make the card clickable (the gallery uses this to open
 * its lightbox); without it the card is plain content. Extra props (data-*
 * attributes for GSAP's Flip, for example) fall through to the root element.
 */
export default function PhotoCard({
	photo,
	onActivate,
	sizes = '(min-width: 62rem) 22rem, (min-width: 36rem) 45vw, 100vw',
	imageSx = {},
	captionSx = {},
	...rest
}) {
	const interactive = Boolean(onActivate);

	return (
		<Box
			component="figure"
			{...rest}
			{...(interactive
				? {
						onClick: onActivate,
						onKeyDown: (event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								onActivate();
							}
						},
						tabIndex: 0,
						role: 'button',
						'aria-label': `View larger: ${photo.alt}`,
					}
				: {})}
			sx={{
				m: 0,
				cursor: interactive ? 'zoom-in' : 'default',
				borderRadius: 2,
				overflow: 'hidden',
				backgroundColor: brand.surface,
				boxShadow: shadows.card,
				transition: 'box-shadow 300ms ease, transform 300ms ease',
				'&:hover': { boxShadow: shadows.lift },
				'&:hover img': { transform: 'scale(1.04)' },
				'@media (prefers-reduced-motion: reduce)': {
					'&:hover img': { transform: 'none' },
				},
			}}
		>
			<Box sx={{ overflow: 'hidden' }}>
				<Img
					
					src={photo.src}
					srcSet={photo.srcset}
					sizes={sizes}
					alt={photo.alt}
					width={photo.width}
					height={photo.height}
					loading="lazy"
					decoding="async"
					sx={{
						width: '100%',
						height: 'auto',
						display: 'block',
						transition: 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)',
						...imageSx,
					}}
				/>
			</Box>

			<Typography
				component="figcaption"
				sx={{ p: 2.5, fontSize: '0.9rem', color: brand.textMuted, ...captionSx }}
			>
				{photo.caption}
			</Typography>
		</Box>
	);
}
